import React, { useEffect, useRef } from 'react';
import { BRAND_COLORS } from './constants';

// Version 5: Pacman Maze Background
export const PacmanBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Configuration state
        let config = {
            pacmanCount: 3,
            ghostCount: 6,
            mazeComplexity: 5,
            speed: 3,
            globalOpacity: 80,
            globalScale: 100
        };

        // Listen for configuration changes
        const handleConfigChange = (event: CustomEvent) => {
            const newConfig = event.detail;
            config = {
                pacmanCount: newConfig.v5PacmanCount || config.pacmanCount,
                ghostCount: newConfig.v5GhostCount || config.ghostCount,
                mazeComplexity: newConfig.v5MazeComplexity || config.mazeComplexity,
                speed: newConfig.v5Speed || config.speed,
                globalOpacity: newConfig.globalOpacity || config.globalOpacity,
                globalScale: newConfig.globalScale || config.globalScale
            };
            reinitializeGame();
        };

        window.addEventListener("backgroundConfigChange", handleConfigChange as EventListener);

        const cellSize = 20;
        let cols = Math.floor(canvas.width / cellSize);
        let rows = Math.floor(canvas.height / cellSize);

        // Maze data (0 = wall, 1 = path)
        let maze: number[][] = [];

        interface Entity {
            x: number;
            y: number;
            direction: { x: number; y: number };
            color: string;
            lastMoveTime: number;
            speed: number;
            id: string;
            type: "pacman" | "ghost";
            mouthAngle: number;
        }

        let pacmans: Entity[] = [];
        let ghosts: Entity[] = [];
        let dots: Array<{ x: number; y: number; type: "normal" | "power" }> = [];

        const pacmanColors = [BRAND_COLORS.primary, "#FFD700", "#FF6B6B"];
        const ghostColors = ["#FF6B6B", "#FFB6C1", "#87CEEB", "#98FB98", "#DDA0DD", "#F0E68C"];

        const generateMaze = () => {
            maze = Array(rows).fill(null).map(() => Array(cols).fill(0));

            const pathDensity = Math.min(0.3 + (config.mazeComplexity / 20), 0.7);

            for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                    if (Math.random() < pathDensity) {
                        maze[y][x] = 1;
                    }
                }
            }

            // Ensure borders are walls
            for (let x = 0; x < cols; x++) {
                maze[0][x] = 0;
                maze[rows - 1][x] = 0;
            }
            for (let y = 0; y < rows; y++) {
                maze[y][0] = 0;
                maze[y][cols - 1] = 0;
            }

            // Add dots to paths
            dots = [];
            for (let y = 1; y < rows - 1; y++) {
                for (let x = 1; x < cols - 1; x++) {
                    if (maze[y][x] === 1 && Math.random() < 0.3) {
                        dots.push({
                            x,
                            y,
                            type: Math.random() < 0.1 ? "power" : "normal"
                        });
                    }
                }
            }
        };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            cols = Math.floor(canvas.width / cellSize);
            rows = Math.floor(canvas.height / cellSize);
            generateMaze();
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);

        const createEntity = (index: number, type: "pacman" | "ghost"): Entity => {
            let x, y;
            do {
                x = Math.floor(Math.random() * (cols - 2)) + 1;
                y = Math.floor(Math.random() * (rows - 2)) + 1;
            } while (maze[y][x] !== 1);

            const colors = type === "pacman" ? pacmanColors : ghostColors;

            return {
                x,
                y,
                direction: { x: 1, y: 0 },
                color: colors[index % colors.length],
                lastMoveTime: 0,
                speed: type === "pacman" ? 200 / config.speed : 250 / config.speed,
                id: `${type}_${index}`,
                type,
                mouthAngle: 0
            };
        };

        const findValidMoves = (x: number, y: number): Array<{ x: number; y: number }> => {
            const moves = [];
            const directions = [
                { x: 0, y: -1 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }
            ];

            for (const dir of directions) {
                const newX = x + dir.x;
                const newY = y + dir.y;

                if (newX >= 0 && newX < cols && newY >= 0 && newY < rows && maze[newY][newX] === 1) {
                    moves.push(dir);
                }
            }

            return moves;
        };

        const moveEntity = (entity: Entity, now: number) => {
            if (now - entity.lastMoveTime < entity.speed) return;

            const validMoves = findValidMoves(entity.x, entity.y);
            if (validMoves.length === 0) return;

            // Simple AI
            if (Math.random() < 0.3) {
                entity.direction = validMoves[Math.floor(Math.random() * validMoves.length)];
            }

            const newX = entity.x + entity.direction.x;
            const newY = entity.y + entity.direction.y;

            if (newX >= 0 && newX < cols && newY >= 0 && newY < rows && maze[newY][newX] === 1) {
                entity.x = newX;
                entity.y = newY;
                entity.lastMoveTime = now;

                if (entity.type === "pacman") {
                    entity.mouthAngle = (entity.mouthAngle + 30) % 360;
                }
            }
        };

        const checkCollisions = () => {
            pacmans.forEach(pacman => {
                dots = dots.filter(dot => !(dot.x === pacman.x && dot.y === pacman.y));
            });

            ghosts.forEach(ghost => {
                pacmans = pacmans.filter(pacman => !(pacman.x === ghost.x && pacman.y === ghost.y));
            });
        };

        const reinitializeGame = () => {
            pacmans = [];
            ghosts = [];

            for (let i = 0; i < config.pacmanCount; i++) {
                pacmans.push(createEntity(i, "pacman"));
            }

            for (let i = 0; i < config.ghostCount; i++) {
                ghosts.push(createEntity(i, "ghost"));
            }
        };

        const initializeGame = () => {
            generateMaze();
            reinitializeGame();
        };

        let animationFrameId: number;

        const gameLoop = (now: number) => {
            ctx.fillStyle = BRAND_COLORS.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const opacity = config.globalOpacity / 100;
            const scale = config.globalScale / 100;

            // Draw maze walls
            for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                    if (maze[y][x] === 0) {
                        ctx.fillStyle = `${BRAND_COLORS.primary}${Math.floor(opacity * 100).toString(16).padStart(2, "0")}`;
                        ctx.fillRect(x * cellSize, y * cellSize, cellSize * scale, cellSize * scale);
                    }
                }
            }

            // Draw dots
            dots.forEach(dot => {
                const dotOpacity = opacity * (dot.type === "power" ? 0.8 : 0.5);
                const dotSize = (dot.type === "power" ? 6 : 3) * scale;

                ctx.fillStyle = `${BRAND_COLORS.secondary}${Math.floor(dotOpacity * 255).toString(16).padStart(2, "0")}`;
                ctx.beginPath();
                ctx.arc(dot.x * cellSize + cellSize / 2, dot.y * cellSize + cellSize / 2, dotSize, 0, Math.PI * 2);
                ctx.fill();
            });

            // Move and draw entities
            [...pacmans, ...ghosts].forEach(entity => {
                moveEntity(entity, now);

                const entityOpacity = opacity * 0.8;
                const entitySize = cellSize * 0.8 * scale;

                ctx.fillStyle = `${entity.color}${Math.floor(entityOpacity * 255).toString(16).padStart(2, "0")}`;

                if (entity.type === "pacman") {
                    ctx.beginPath();
                    const centerX = entity.x * cellSize + cellSize / 2;
                    const centerY = entity.y * cellSize + cellSize / 2;
                    const radius = entitySize / 2;

                    let mouthDirection = 0;
                    if (entity.direction.x > 0) mouthDirection = 0;
                    else if (entity.direction.x < 0) mouthDirection = Math.PI;
                    else if (entity.direction.y > 0) mouthDirection = Math.PI / 2;
                    else if (entity.direction.y < 0) mouthDirection = -Math.PI / 2;

                    const mouthOpen = Math.sin(entity.mouthAngle * Math.PI / 180) > 0;
                    const mouthAngle = mouthOpen ? Math.PI / 3 : 0;

                    ctx.arc(centerX, centerY, radius, mouthDirection + mouthAngle, mouthDirection - mouthAngle);
                    ctx.lineTo(centerX, centerY);
                    ctx.fill();
                } else {
                    ctx.beginPath();
                    ctx.arc(entity.x * cellSize + cellSize / 2, entity.y * cellSize + cellSize / 2, entitySize / 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            });

            checkCollisions();

            // Respawn entities occasionally
            if (pacmans.length < config.pacmanCount && Math.random() < 0.01) {
                pacmans.push(createEntity(pacmans.length, "pacman"));
            }

            if (ghosts.length < config.ghostCount && Math.random() < 0.01) {
                ghosts.push(createEntity(ghosts.length, "ghost"));
            }

            // Add more dots occasionally
            if (dots.length < 10 && Math.random() < 0.02) {
                let x, y;
                do {
                    x = Math.floor(Math.random() * (cols - 2)) + 1;
                    y = Math.floor(Math.random() * (rows - 2)) + 1;
                } while (maze[y][x] !== 1);

                dots.push({ x, y, type: Math.random() < 0.1 ? "power" : "normal" });
            }

            animationFrameId = requestAnimationFrame(gameLoop);
        };

        initializeGame();
        animationFrameId = requestAnimationFrame(gameLoop);

        return () => {
            window.removeEventListener("resize", resizeCanvas);
            window.removeEventListener("backgroundConfigChange", handleConfigChange as EventListener);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
            style={{ background: BRAND_COLORS.background }}
        />
    );
};
